<template>
  <InputSkeleton ref="inputRef"
                 :name="name"
                 :errors="errors"
                 class="SelectInput"
                 :class="{
                   [`SelectInput--${mode}`]: !!mode,
                   [`SelectInput--${size}`]: !!size,
                   'SelectInput--opened': isOpened,
                   'SelectInput--invalid': isInvalid,
                   'SelectInput--inline': inline,
                   'SelectInput--disabled': disabled,
                   'SelectInput--required': required,
                   'SelectInput--highlight': isHighlighted,
                   'SelectInput--outlined': outlined,
                   [`SelectInput--theme-${theme}`]: !!theme,
                   'SelectInput--filled': Boolean(filter && !isMultiple),
                   'SelectInput--float-label': floatLabel,
                 }">
    <input v-if="!isMultiple"
           type="hidden"
           :value="modelValue"
           :name="name"
    />
    <template v-if="$slots.default || label" #label>
      <slot v-if="$slots.default" />
      <template v-else>{{ label }}</template>
    </template>
    <template v-if="mode === SelectInputMode.DEFAULT">
      <div class="SelectInput__custom">
        <div class="SelectInput__custom_select" :class="{ 'SelectInput__custom_select--opened': isOpened }">
          <div ref="elIgnore" class="SelectInput__container" @click="onToggleOpened(false)">
            <template v-if="isShowSelectedCount && displayedSelectedOptionsTitle">
              <label class="SelectInput__search">
                <span class="SelectInput__selected">{{ displayedSelectedOptionsTitle }}</span>
                <span v-if="displayedSelectedOptionsCount" class="SelectInput__count-selected">{{ displayedSelectedOptionsCount }}</span>
                <Icon name="chevron.bottom"
                      class="SelectInput__arrow"
                />
              </label>
              <div class="SelectInput__input">
                <input type="hidden" :placeholder="placeholder" :value="selectedOptionsTitle" />
              </div>
            </template>
            <template v-else>
              <TextInput :model-value="selectedOptionsTitle"
                         name=""
                         :size="size"
                         :placeholder="placeholder"
                         :invalid="isInvalid"
                         readonly
                         class="SelectInput__input"
              />
              <slot name="selected"
                    :selected="selectedOptionsTitle"
              />
              <Icon name="chevron.bottom"
                    class="SelectInput__arrow"
                    @click.stop="onToggleOpened(true)"
              />
            </template>
            <span v-if="isShowSelectedCount" ref="helperTitleSpanRef" class="SelectInput__helper-title-span" />
          </div>
          <OnClickOutside v-if="elIgnore"
                          :options="{ ignore: [elIgnore] }"
                          data-test-id="options"
                          @trigger="onClose">
            <div v-if="isOpened && !disabled" class="SelectInput__custom_options">
              <template v-if="isMultiple">
                <template v-if="checkboxSelectAll">
                  <div class="SelectInput__custom_option">
                    <CheckBoxInput :model-value="isCheckedCheckboxSelectAll"
                                   name=""
                                   :disabled="disabled"
                                   :label="checkboxSelectAllName"
                                   :value="inputName"
                                   @change="toggleSelectedAll"
                    />
                  </div>
                  <hr class="SelectInput__divider" />
                </template>
                <template v-if="whatever">
                  <div class="SelectInput__custom_option">
                    <CheckBoxInput :model-value="isCheckedWhatever"
                                   :disabled="disabled"
                                   :name="inputName"
                                   @update:model-value="onChangeWhatever">
                      {{ whateverTitle }}
                    </CheckBoxInput>
                  </div>
                  <hr class="SelectInput__divider" />
                </template>
                <template v-if="groupedOptions.length">
                  <div v-for="group in groupedOptions" :key="group.value" class="SelectInput__items">
                    <button type="button"
                            class="SelectInput__group"
                            :class="{ 'SelectInput__group--opened' : group.opened }"
                            data-test-id="group-button"
                            @click="onGroupToggle(group.value)">
                      {{ group.value }}
                      <Icon name="chevron.bottom" />
                    </button>
                    <template v-if="group.opened">
                      <div v-for="option in group.items" :key="option.key" class="SelectInput__custom_option">
                        <CheckBoxInput :model-value="isActiveOption(option)"
                                       :name="option.title"
                                       :label="option.title"
                                       :value="option.value"
                                       :disabled="whatever && isCheckedWhatever"
                                       @update:model-value="onChange(option)"
                        />
                      </div>
                    </template>
                  </div>
                </template>
                <template v-else>
                  <slot name="options">
                    <div>
                      <template v-for="option in multipleOptions(visibleOptions)" :key="option.key">
                        <div v-if="option.type !== OptionType.DIVIDER && option.isActive"
                             class="SelectInput__custom_option">
                          <CheckBoxInput :model-value="isActiveOption(option)"
                                         :disabled="disabled || option.disabled"
                                         :name="option.title"
                                         :label="option.title"
                                         :value="option.value"
                                         @update:model-value="onChange(option)"
                          />
                        </div>
                        <hr v-else-if="option.type === OptionType.DIVIDER && option.isActive"
                            class="SelectInput__divider"
                        />
                      </template>
                    </div>
                  </slot>
                </template>
              </template>
              <template v-else-if="$slots.option">
                <div v-for="option in visibleOptions"
                     :key="option.key"
                     class="SelectInput__slot-option"
                     :class="{
                       'SelectInput__slot-option--selected': option.value === modelValue,
                       'SelectInput__slot-option--disabled': option.disabled
                     }"
                     @click.stop.self="onChange(option)">
                  <div class="SelectInput__slot-option-content">
                    <slot name="option" :option="option" />
                  </div>
                </div>
              </template>
              <template v-else>
                <div v-for="option in visibleOptions"
                     :key="option.key"
                     class="SelectInput__custom_option"
                     :class="{
                       'SelectInput__custom_option--selected': option.value === modelValue,
                       'SelectInput__custom_option--disabled': option.disabled,
                     }"
                     data-test-id="option"
                     @click="onChange(option)">
                  {{ option.title }}
                  <div v-if="option.hint" class="SelectInput__custom_option_hint">{{ option.hint }}</div>
                </div>
              </template>
            </div>
          </OnClickOutside>
        </div>
      </div>
    </template>
    <template v-if="mode === SelectInputMode.FILTER">
      <div class="SelectInput__custom SelectInput__custom--filter">
        <div class="SelectInput__custom_select" :class="{ 'SelectInput__custom_select--opened': isOpened }">
          <div ref="elFilterIgnore"
               class="SelectInput__container"
               :class="{ 'SelectInput__container--disabled': disabled }"
               @click="onToggleOpened(false)">
            <TextInput v-model="filter"
                       :name="inputName"
                       :disabled="disabled"
                       :placeholder="placeholder"
                       autocomplete="off"
                       class="SelectInput__input"
                       @focusin="onFocusFilterInput"
                       @update:model-value="onFilterInput"
            />
            <Icon name="chevron.bottom"
                  class="SelectInput__arrow"
                  @click.stop="onToggleOpened(true)"
            />
            <button v-if="filter && !isMultiple && !disabled"
                    name="clear-filter"
                    type="button"
                    class="SelectInput__clear"
                    :disabled="disabled"
                    @click="onClear">
              <Icon name="times-r" />
            </button>
          </div>
          <div v-if="$slots.option" v-show="focus && options.length" class="SelectInput__slot-datalist">
            <div v-for="option in visibleOptions"
                 :key="option.key"
                 class="SelectInput__slot-option"
                 :class="{
                   'SelectInput__slot-option--selected': option.value === modelValue,
                   'SelectInput__slot-option--disabled': option.disabled
                 }"
                 @click.stop.self="onChange(option)">
              <div class="SelectInput__slot-option-content">
                <slot name="option" :option="option" />
              </div>
            </div>
          </div>
          <template v-if="isMultiple">
            <OnClickOutside v-if="isOpened"
                            :options="{ ignore: [elFilterIgnore] }"
                            data-test-id="options"
                            @trigger="onCloseFilterSuggestion">
              <div class="SelectInput__custom_options">
                <template v-if="checkboxSelectAll">
                  <div class="SelectInput__custom_option">
                    <CheckBoxInput :model-value="isCheckedCheckboxSelectAll"
                                   :name="`${inputName}::SelectAll`"
                                   :disabled="disabled"
                                   :label="checkboxSelectAllName"
                                   :value="inputName"
                                   @click.stop
                                   @change="toggleSelectedAll"
                    />
                  </div>
                  <hr class="SelectInput__divider" />
                </template>
                <template v-if="whatever">
                  <div class="SelectInput__custom_option">
                    <CheckBoxInput :model-value="isCheckedWhatever"
                                   :disabled="disabled"
                                   :name="`${inputName}::whatever`"
                                   @click.stop
                                   @update:model-value="onChangeWhatever">
                      {{ whateverTitle }}
                    </CheckBoxInput>
                  </div>
                  <hr class="SelectInput__divider" />
                </template>
                <slot name="options">
                  <div>
                    <template v-for="option in multipleOptions(visibleOptions)" :key="option.key">
                      <div v-if="option.type !== OptionType.DIVIDER && option.isActive"
                           class="SelectInput__custom_option">
                        <CheckBoxInput :model-value="isActiveOption(option)"
                                       :disabled="disabled || option.disabled"
                                       :name="option.title"
                                       :label="option.title"
                                       :value="option.value"
                                       @click.stop
                                       @update:model-value="onChange(option)"
                        />
                      </div>
                      <hr v-else-if="option.type === OptionType.DIVIDER && option.isActive"
                          class="SelectInput__divider"
                      />
                    </template>
                  </div>
                </slot>
              </div>
            </OnClickOutside>
          </template>
          <template v-if="!$slots.option && !isMultiple && !disabled">
            <datalist v-show="focus && options.length">
              <option v-for="option in visibleOptions"
                      :key="option.key"
                      :value="option.value"
                      :disabled="option.disabled"
                      @click.stop.self="onChange(option)">
                {{ option.title }}{{ option.hint ? ` (${option.hint})` : '' }}
              </option>
            </datalist>
          </template>
        </div>
      </div>
    </template>
  </InputSkeleton>
</template>

<script setup lang="ts" generic="T extends string | number | boolean">
  import { OnClickOutside } from '@vueuse/components';
  import { chain } from 'lodash';
  import { computed, nextTick, onBeforeMount, onMounted, ref, useSlots, watch, onUnmounted } from 'vue';
  import { SelectOption } from '@pn/common';
  import { implode, randomString, toggleArrayValue } from '@pn/utils';
  import { UiSize } from '../../../constants';
  import Icon from '../../Icon.vue';
  import InputSkeleton from '../InputSkeleton.vue';
  import CheckBoxInput from './CheckBoxInput.vue';
  import TextInput from './TextInput.vue';
  import { InputErrorObject, SelectInputMode } from './types';

  type SelectInputOption = SelectOption<T> & {
    readonly disabled?: boolean;
    readonly group?: string;
    readonly hint?: string;
  };

  const props = withDefaults(defineProps<{
    modelValue?: T | T[];
    name: string;
    options: SelectInputOption[];
    placeholder?: string;
    mode?: SelectInputMode;
    size?: UiSize;
    label?: string;
    disabled?: boolean;
    readonly?: boolean;
    required?: boolean;
    highlight?: boolean;
    inline?: boolean;
    invalid?: boolean;
    errors?: InputErrorObject[];
    outlined?: boolean;
    multiple?: boolean;
    checkboxSelectAllName?: string;
    checkboxSelectAll?: boolean;
    selectedAllLabel?: string;
    emptyLabel?: string;
    whatever?: boolean;
    whateverTitle?: string;
    theme?: 'second' | 'primary' | 'danger';
    maxRows?: number;
    priorityForSelected?: boolean;
    floatLabel?: boolean;
    showSelectedCount?: boolean;
  }>(), {
    modelValue: null,
    mode: SelectInputMode.DEFAULT,
    size: UiSize.MD,
    errors: () => [],
    checkboxSelectAllName: 'Выбрать все',
    whateverTitle: 'Неважно',
    theme: 'primary',
    priorityForSelected: false,
    floatLabel: false,
    placeholder: '',
  });

  const emit = defineEmits<{
    (e: 'update:model-value', modelValue: T | T[]): void;
    (e: 'update:filter-value', value: string | string[]): void;
  }>();

  enum OptionType {
    OPTION = 'option',
    DIVIDER = 'divider',
  }

  type SelectInputOptionWithKey = SelectInputOption & { readonly key: string };

  type MultipleInputOption = SelectInputOptionWithKey & {
    readonly type: OptionType.OPTION;
  };

  type MultipleInputItem = (MultipleInputOption | {
    readonly type: OptionType.DIVIDER;
  }) & {
    readonly key: string;
    readonly isActive?: boolean;
  };

  const isOpened = ref(false);
  const isCheckedWhatever = ref(true);
  const filter = ref('');
  const filterInput = ref<HTMLInputElement>(null);
  const focus = ref(false);
  const slots = useSlots();
  const elIgnore = ref<HTMLElement | null>(null);
  const elFilterIgnore = ref<HTMLElement | null>(null);

  const inputWidth = ref(0);
  const inputRef = ref<typeof InputSkeleton>(null);
  const helperTitleSpanRef = ref<HTMLElement | null>(null);
  const displayedSelectedOptionsTitle = ref('');
  const displayedSelectedOptionsCount = ref(0);

  const checkIsMultiple = (value: T | T[]): value is T[] => props.multiple && Array.isArray(value);

  const isMultiple = computed(() => checkIsMultiple(props.modelValue));
  const isEmpty = computed(() => {
    if (checkIsMultiple(props.modelValue)) {
      return props.modelValue.length === 0;
    }

    return !props.modelValue;
  });
  const isInvalid = computed(() => props.invalid || !!props.errors.length);
  const isHighlighted = computed(() => Boolean(props.highlight && isEmpty.value));
  const inputName = computed(() => checkIsMultiple(props.modelValue) ? `${props.name}[]` : props.name);
  const selectedOption = computed<SelectInputOption>(() => {
    if (checkIsMultiple(props.modelValue)) {
      return null;
    }

    return props.options.find(({ value }) => props.modelValue === value);
  });

  const selectedOptions = computed(() => {
    if (checkIsMultiple(props.modelValue)) {
      return props.modelValue.reduce((acc, value) => {
        const option = props.options.find((el) => el.value.toString() === value.toString());

        return [...acc, option?.title];
      }, []);
    }

    return [];
  });

  const selectedOptionsTitle = computed(() => {
    if (checkIsMultiple(props.modelValue) && props.modelValue.length === 0 && props.emptyLabel) {
      return props.emptyLabel;
    }

    if (
      checkIsMultiple(props.modelValue)
      && props.modelValue.length === props.options.length
      && props.selectedAllLabel
    ) {
      return props.selectedAllLabel;
    }

    if (checkIsMultiple(props.modelValue)) {
      return selectedOptions.value.join(', ');
    }

    return selectedOption.value?.value ? selectedOption.value.title : '';
  });

  const isCheckedCheckboxSelectAll = computed(
    () => checkIsMultiple(props.modelValue) ? props.modelValue.length === props.options.length : false,
  );

  const isActiveOption = (option: SelectInputOption): boolean => {
    if (checkIsMultiple(props.modelValue)) {
      return (props.modelValue.length === 0 && option.value === null) || props.modelValue.includes(option.value);
    }

    return props.modelValue === option.value;
  };

  // TODO: пофиксить метод, чтобы не фильтровать уже отфильтрованные опции
  const visibleOptions = computed<SelectInputOptionWithKey[]>(
    () => chain(props.options)
      .filter((item) => {
        if (slots.option) {
          return true;
        }

        return props.mode === SelectInputMode.FILTER
          ? item.title.toLowerCase().includes(filter.value.toLowerCase())
          : true;
      })
      .map((item) => ({
        ...item,
        key: randomString(32),
      }))
      .value(),
  );

  const multipleOptions = (options: SelectInputOptionWithKey[]): MultipleInputItem[] => {
    if (props.priorityForSelected) {
      return [
        ...options.filter((option) => isActiveOption(option)).map((option) => (
          {
            ...option,
            type: OptionType.OPTION,
            isActive: true,
          })),
        {
          type: OptionType.DIVIDER,
          key: randomString(32),
          isActive: !!options.length
            && options.filter((option) => isActiveOption(option)).length !== 0
            && options.filter((option) => !isActiveOption(option)).length !== 0,
        },
        ...options.filter((option) => !isActiveOption(option)).map((option) => (
          {
            ...option,
            type: OptionType.OPTION,
            isActive: true,
          })),
      ];
    }
    return options.map((option) => ({
      ...option,
      type: OptionType.OPTION,
      isActive: true,
    }));
  };

  const getGroups = (options: SelectInputOption[]) => chain(options)
    .map((o) => o.group)
    .filter((o) => !!o)
    .uniq()
    .value();
  const openedGroups = ref<string[]>([]);
  const groups = computed(() => getGroups(props.options));
  const groupedOptions = computed(() => groups.value.map((group) => ({
    value: group,
    opened: openedGroups.value.includes(group),
    items: visibleOptions.value.filter((o) => o.group === group),
  })));
  const onGroupToggle = (group: string) => {
    openedGroups.value = openedGroups.value.includes(group)
      ? openedGroups.value.filter((g) => g !== group)
      : [...openedGroups.value, group];
  };
  const maxHeightCustomList = computed(() => props.maxRows ? `${props.maxRows * 28 + 20}px` : '300px');

  const isShowSelectedCount = computed(() => isMultiple.value && props.showSelectedCount);

  const focusOut = () => {
    focus.value = false;
  };
  const onClose = () => {
    isOpened.value = false;
  };

  const onFocusFilterInput = () => {
    focus.value = true;

    if (isMultiple.value) {
      filter.value = '';
    }
  };

  const onCloseFilterSuggestion = () => {
    onClose();
    filter.value = selectedOptionsTitle.value;
  };

  const onClear = () => {
    if (props.mode === SelectInputMode.FILTER) {
      emit('update:model-value', isMultiple.value ? [] : null);
      emit('update:filter-value', isMultiple.value ? [] : '');
      filter.value = '';
    }
  };

  const onChange = async (option: SelectInputOption) => {
    if (props.disabled || props.readonly || option.disabled) {
      return;
    }

    let nextValue;

    if (checkIsMultiple(props.modelValue)) {
      if (option.value === null) {
        nextValue = [];
      } else {
        nextValue = toggleArrayValue(option.value, props.modelValue);
      }
    } else {
      nextValue = option.value;
    }

    emit('update:model-value', nextValue);

    if (props.mode === SelectInputMode.DEFAULT && !isMultiple.value) {
      onClose();
    }

    if (props.mode === SelectInputMode.FILTER) {
      filter.value = isMultiple.value ? '' : option.title;

      if (isMultiple.value) {
        await nextTick(() => {
          filterInput.value?.focus();
        });
      } else {
        focusOut();
      }
    }
  };

  const onFilterInput = (value: string) => {
    emit('update:filter-value', value);

    if (!value) {
      onClear();
    }
  };

  const toggleSelectedAll = () => {
    if (!checkIsMultiple(props.modelValue)) {
      return;
    }

    const nextValue = props.modelValue.length && isCheckedCheckboxSelectAll.value
      ? []
      : props.options.map((option) => option.value);

    emit('update:model-value', nextValue);
  };

  const onChangeWhatever = () => {
    isCheckedWhatever.value = !isCheckedWhatever.value;

    if (isCheckedWhatever.value) {
      emit('update:model-value', checkIsMultiple(props.modelValue) ? [] : null);
    }
  };

  const onToggleOpened = (arrowClicked: boolean) => {
    if (props.disabled) {
      return;
    }

    /** необходимо для того чтобы подсказки не скрывались при фокусе на инпуте **/
    if (isMultiple.value && isOpened.value && !arrowClicked) {
      return;
    }

    isOpened.value = !isOpened.value;
  };

  function updateDisplayedSelectedOptionsTitle(): void {
    if (!helperTitleSpanRef.value || inputWidth.value === 0) {
      displayedSelectedOptionsTitle.value = '';
      displayedSelectedOptionsCount.value = 0;

      return;
    }

    let count = 0;
    const displayItems = [];
    const titles = selectedOptions.value;

    for (const title of titles) {
      displayItems.push(title);
      helperTitleSpanRef.value.textContent = displayItems.join(', ');

      if ((helperTitleSpanRef.value.offsetWidth > inputWidth.value - 20) && displayItems.length > 1) {
        count = titles.length - displayItems.length + 1;
        displayItems.pop();
        displayedSelectedOptionsTitle.value = displayItems.join(', ');
        displayedSelectedOptionsCount.value = count;

        return;
      }
    }

    displayedSelectedOptionsCount.value = count;
    displayedSelectedOptionsTitle.value = displayItems.join(', ');
  }

  const updateInputWidth = () => {
    if (inputRef.value) {
      inputWidth.value = inputRef.value.$el.clientWidth;
      updateDisplayedSelectedOptionsTitle();
    }
  };

  watch(() => props.modelValue, async () => {
    if (isCheckedWhatever.value && checkIsMultiple(props.modelValue) && props.modelValue.length) {
      isCheckedWhatever.value = false;
    }

    if (isShowSelectedCount.value) {
      await nextTick();
      updateInputWidth();
    }
  }, { immediate: true });

  watch(() => props.options, async () => {
    // TODO: поправить баг удаления фильтра
    if (props.mode !== SelectInputMode.FILTER && props.modelValue && !slots.option) {
      filter.value = isMultiple.value
        ? selectedOptionsTitle.value
        : selectedOption.value?.title || '';
    }

    if (isShowSelectedCount.value) {
      await nextTick();
      updateInputWidth();
    }
  }, { immediate: true });

  onBeforeMount(() => {
    openedGroups.value = getGroups(props.options);

    if (slots.options) {
      // eslint-disable-next-line no-console
      console.warn(implode(' ', '[deprecated]', 'SelectInput', ' ~ ', 'Удалить слот `options`'));
    }

    if (selectedOption.value && props.mode === SelectInputMode.FILTER) {
      filter.value = selectedOption.value?.title || '';
    }
  });

  onMounted(async () => {
    if (isMultiple.value) {
      filterInput.value = document.getElementById(inputName.value) as HTMLInputElement;
    }

    if (isMultiple.value && isShowSelectedCount.value) {
      await nextTick(() => {
        updateInputWidth();
        window.addEventListener('resize', updateInputWidth);
      });
    }
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateInputWidth);
  });
</script>

<style lang="scss" scoped>
  @import '../../../assets/scss/_variables.scss';
  @import '../../../assets/scss/_mixins.scss';

  .SelectInput {
    // ...

    &__divider {
      background-color: $color-grey-75;
      width: 100%;
      height: 1px;
      margin: 4px 0;
    }

    &__input:deep(input)  {
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &--required &__input {
      background-color: $color-light-yellow;
    }

    &--disabled &__input,
    &--disabled &__custom_select:deep(input) {
      background-color: $color-grey-50;
      cursor: not-allowed;
    }

    // region DEFAULT
    &--filter  &__container,
    &--default &__container {
      // ...

      &:after {
        position: absolute;
        content: '';
        right: 28px;
        top: 50%;
        transform: translateY(-50%);
        bottom: 4px;
        height: calc(100% - 8px);
        width: 1px;
        background-color: $color-grey-75;
        pointer-events: none;
      }

      &:deep(.TextInput) {
        height: 100%;
      }
    }

    &__option {
      color: $color-primary;

      &--placeholder {
        color: $color-grey-100;
      }
    }

    &--filter  &__arrow,
    &--default &__arrow {
      @include transition($transition-function, $transition-duration, transform);

      position: absolute;
      color: $color-grey-100;
      height: 30px;
      width: 30px;
      right: 0;
      top: 50%;
      padding: 10px;
      transform: translateY(-50%);
      cursor: pointer;
    }

    &__custom {
      min-height: 28px;
    }

    &__custom_label {
      font-weight: 600;
      margin-bottom: 10px;
    }

    &__custom_select {
      position: relative;
    }

    &__custom_select:deep(input) {
      padding-right: 35px;
      min-height: 28px;
    }

    &__custom_options {
      @include custom-scroll();

      position: absolute;
      width: 100%;
      padding: 10px;
      background-color: $color-white;
      border: 1px solid $color-grey-75;
      border-top: none;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      z-index: 21;
      max-height: v-bind(maxHeightCustomList);
      overflow: hidden;
      overflow-y: auto;
      scrollbar-width: thin;
    }

    &__custom_option_range {
      margin-bottom: 16px;
    }

    &__custom_option {
      height: 28px;
      padding: 0 5px;
      border-radius: 8px;
      line-height: 28px;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      cursor: pointer;
      user-select: none;

      &:hover {
        background-color: $blue-light;
      }

      &--disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    &__custom_option_hint {
      color: $color-second;
    }

    &__custom_option--selected {
      background-color: $blue-light;
    }

    &__custom_select:deep(input) {
      background-color: $color-white;
    }

    &__custom_select--opened &__arrow {
      transform: translateY(-50%) rotate(180deg);
    }

    &__custom_select--opened &__container:deep(input) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    // endregion DEFAULT

    // region FILTER

    &--filter &__container {
      position: relative;
      height: 100%;
      min-height: 28px;
      display: flex;
      flex-direction: column;
    }

    &--filter[class*="--filled"] &__container,
    &--filter[class*="--filled"] datalist {
      width: calc(100% - 30px);

      &--disabled {
        width: 100%;
      }
    }

    &--filter input {
      flex-grow: 1;
      display: block;
      height: 100%;
      width: 100%;
      padding-left: 10px;
      border-radius: 4px;
      border: 1px solid $color-grey-75;
      background-color: $color-white;
      appearance: none;
      outline: none;
      cursor: pointer;

      &:focus {
        outline: none;
      }
    }

    &#{&}--filter#{&}--highlight input,
    &--highlight:deep(input) {
      background-color: $color-light-yellow;
    }

    &__slot-option {
      padding: 5px;
      border-radius: 8px;
      cursor: pointer;
      user-select: none;
      text-overflow: ellipsis;
      overflow: hidden;

      &-content {
        pointer-events: none;
      }

      &--selected {
        background-color: $color-blue-25;
      }

      &--disabled {
        cursor: not-allowed;

        .SelectInput__slot-option-content {
          opacity: 0.4;
        }
      }

      &:hover {
        background-color: $blue-light;
      }
    }

    &--filter datalist,
    &__slot-datalist {
      display: block;
      position: absolute;
      top: calc(100% - 2px);
      z-index: 2;
      background-color: $color-white;
      border: 1px solid $color-grey-75;
      border-radius: 0 0 4px 4px;
      width: 100%;
      padding: 5px;
      max-height: 300px;
      overflow: auto;

      @include scrollbar-vertical();
    }

    &--filter input:focus ~ datalist {
      display: block;
    }

    &--filter option {
      background-color: $color-white;
      padding: 5px;
      border-radius: 4px;
      cursor: pointer;
    }

    &--filter option:hover {
      background-color: $color-grey-50;
    }

    &--filter option:hover:disabled {
      background-color: $color-white;
      cursor: not-allowed;
    }

    &--filter option:disabled {
      pointer-events: none;
    }

    &--filter &__clear {
      @include transition($transition-function, $transition-duration, transform);
      position: absolute;
      background: transparent;
      padding: 0;
      color: $color-grey-100;
      outline: none;
      height: 10px;
      width: 10px;
      right: calc(((28px - 16px) / 2) + 1px - 30px);
      top: calc(((28px - 16px) / 2) + 1px);

      &:disabled {
        cursor: not-allowed;
      }

      svg {
        height: 10px;
        width: 10px;
        display: block;
      }
    }

    // endregion FILTER

    &__group {
      font-weight: 600;
      font-size: 16px;
      line-height: 22px;
      color: $color-primary;
      background-color: $color-light-blue;
      width: fit-content;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      border-radius: 8px;

      .svg-icon {
        width: 12px;
        height: 8px;

        @include transition($transition-function, $transition-duration, transform);
      }

      &--opened .svg-icon {
        transform: rotate(180deg);
      }
    }

    &--float-label {
      position: relative;

      @include float-label-label();

      .SelectInput__input:deep(input) {
        &::placeholder {
          opacity: 0;

          @include transition($transition-function, $transition-duration, opacity);
        }

        &:focus::placeholder {
          opacity: 1;
        }
      }

      @include float-label-input(unquote('.SelectInput__input input'));

      &.SelectInput--invalid {
        @include float-label-invalid(unquote('.SelectInput__input input'));
      }

      &.SelectInput--highlight {
        @include float-label-highlight(unquote('.SelectInput__input input'));
      }

      &.SelectInput--disabled {
        @include float-label-disabled(unquote('.SelectInput__input input'));
      }
    }

    &__helper-title-span {
      position: absolute;
      white-space: nowrap;
      padding-left: 10px;
      padding-right: 40px;
      visibility: hidden;
      opacity: 0;
      font-size: 14px;
    }

    &__search {
      display: flex;
      align-items: center;
      gap: 5px;
      width: 100%;
      height: 40px;
      padding: 0 35px 0 10px;
      overflow: hidden;
      border-radius: 4px;
      border: 1px solid $color-grey-75;
      background-color: $color-white;
    }

    &--disabled &__search {
      background-color: $color-grey-50;
    }

    &__count-selected {
      padding: 0 6px;
      border-radius: 50%;
      background-color: $blue-light;
      text-align: center;
      font-weight: 400;
      color: $color-second;
    }
  }
</style>
